import { IconButton, InputAdornment } from '@mui/material';
import { useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import Iconify from '../../../../reusable-components/iconify';
import { formStyle } from '../TransitionStyles';
import { validateDate } from '../ValidateFunctions';

FormDatePickerField.propTypes = {
  fieldName: PropTypes.string,
  fieldAttributes: PropTypes.object,
  focusedField: PropTypes.string,
  setFocusedField: PropTypes.func,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
};

export default function FormDatePickerField({
  fieldName,
  fieldAttributes,
  focusedField,
  setFocusedField,
  minDate = null,
  maxDate = null,
}) {
  const { control } = useFormContext();
  const [open, setOpen] = useState(false);
  const isCanceled = useSelector((state) => state.transitions?.data?.isCanceled);

  const fieldDisabled = isCanceled || !fieldAttributes.isEditable;

  return (
    <Controller
      name={fieldName}
      control={control}
      rules={{
        required: fieldAttributes?.validation?.required ?? undefined,
        validate: (value) => validateDate(value, fieldAttributes?.validation?.required, minDate, maxDate),
      }}
      render={({ field, fieldState }) => (
        <DatePicker
          value={field.value ? dayjs(field.value).toDate() : null}
          onChange={(newValue) => {
            const normalized = newValue ? dayjs(newValue).format('YYYY-MM-DD') : null;
            field.onChange(normalized);
          }}
          maxDate={maxDate}
          minDate={minDate}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          disabled={fieldDisabled}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          slotProps={{
            textField: {
              key: field.id,
              fullWidth: true,
              placeholder: !fieldDisabled ? 'MM/DD/YYYY' : '',
              size: 'small',
              error: !!fieldState.error,
              helperText: fieldState.error?.message,
              onFocus: () => setFocusedField(fieldName),
              onBlur: () => setFocusedField(null),
              onClick: () => setOpen(true),
              sx: formStyle,
              InputProps: {
                endAdornment: (
                  <>
                    {!fieldDisabled && (
                      <InputAdornment position="end">
                        <IconButton size="small" edge="end" tabIndex={-1} onClick={() => setOpen(true)}>
                          <Iconify icon="eva:calendar-outline" />
                        </IconButton>
                      </InputAdornment>
                    )}

                    {field.value && focusedField === fieldName && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          edge="end"
                          tabIndex={-1}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpen(false);
                            field.onChange(null);
                          }}
                        >
                          <Iconify icon="eva:close-outline" />
                        </IconButton>
                      </InputAdornment>
                    )}
                  </>
                ),
              },
            },
          }}
        />
      )}
    />
  );
}
